import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { GuardFallback } from './common/GuardFallback';
import { ADMIN_CATALOG_BASE, adminCatalogPath } from '../pages/catalog/catalogPaths';

/**
 * Guard for catalog surfaces that are cross-brand by nature (Tracking).
 * Same admin gate as CatalogRoute, but WITHOUT the brand gate: these pages read
 * every brand at once, so they never carry a brandId and never touch the
 * shared 'admin' bucket. Brand users are redirected exactly like CatalogRoute,
 * and a brand-shell URL is rewritten to its admin twin exactly like CatalogRoute.
 */
export const CatalogAdminRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { canOperateCatalog, user } = usePermissions();
  const { error, refetch } = useCurrentUser();
  const { pathname, search } = useLocation();

  if (!user) {
    return <GuardFallback error={error} onRetry={refetch} />;
  }

  if (!canOperateCatalog()) {
    return <Navigate to="/reports" replace />;
  }

  if (!pathname.startsWith(ADMIN_CATALOG_BASE)) {
    return <Navigate to={adminCatalogPath(pathname, search)} replace />;
  }

  return children;
};

export default CatalogAdminRoute;
